import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { parseAmericanDate } from '@/utils/date-util'
import {
  mustBeInThePast,
  mustBeValidEin,
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
} from '@/utils/validx-util'
import { formatProfileDate } from '@/features/jobs/info-gathering/info-gathering-formatters'
import type { BusinessInfo } from './BusinessInfo'

/**
 * View model for editing business information.
 */
export class BusinessInfoEditorViewModel {
  @observable private accessor _businessName = ''
  @observable private accessor _dateOfIncorporation = ''
  @observable private accessor _employerIdentificationNumber = ''

  readonly validation = validationContext(this, {
    businessName: [
      required('Business name is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Business name must not exceed 100 characters'),
    ],
    dateOfIncorporation: [
      required('Date of incorporation is required'),
      mustBeInThePast('Date of incorporation must be in the past'),
    ],
    employerIdentificationNumber: [
      required('EIN is required'),
      mustBeValidEin(),
    ],
  })

  @computed get businessName() {
    return this._businessName
  }

  set businessName(value: string) {
    this._businessName = value
    this.validation.clearErrors('businessName')
  }

  /**
   * The date of incorporation as typed, in `MM/dd/yyyy`.
   */
  @computed get dateOfIncorporation() {
    return this._dateOfIncorporation
  }

  set dateOfIncorporation(value: string) {
    this._dateOfIncorporation = value
    this.validation.clearErrors('dateOfIncorporation')
  }

  @computed get employerIdentificationNumber() {
    return this._employerIdentificationNumber
  }

  set employerIdentificationNumber(value: string) {
    this._employerIdentificationNumber = value
    this.validation.clearErrors('employerIdentificationNumber')
  }

  /**
   * Resets the state of the view model to that of the given business info,
   * or defaults if none is specified.
   */
  @action.bound
  reset(info?: BusinessInfo) {
    this.businessName = info?.businessName ?? ''
    this.dateOfIncorporation = formatProfileDate(
      info?.dateOfIncorporation ?? null,
    )
    this.employerIdentificationNumber = info?.employerIdentificationNumber ?? ''
    this.validation.reset()
  }

  /**
   * Maps the state of the VM to {@link BusinessInfo}.
   */
  toBusinessInfo(): BusinessInfo {
    return {
      businessName: this.businessName,
      dateOfIncorporation: parseAmericanDate(this.dateOfIncorporation),
      employerIdentificationNumber: this.employerIdentificationNumber,
    }
  }
}
